/**
 * OWL2 IRI Types
 *
 * Represents IRIs (Internationalized Resource Identifiers) as used in OWL2.
 * Supports both full IRIs and abbreviated (prefixed) IRIs.
 */

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * Full IRI - complete URI string
 * Example: "http://example.org/ontology#Person"
 */
export interface FullIRI {
    readonly kind: 'FullIRI';
    readonly value: string;
}

/**
 * Abbreviated IRI - prefixed form using namespace prefix
 * Example: "ex:Person" where ex = "http://example.org/ontology#"
 */
export interface AbbreviatedIRI {
    readonly kind: 'AbbreviatedIRI';
    readonly prefix: string;
    readonly localName: string;
}

/**
 * Discriminated union of IRI representations.
 * The 'kind' field distinguishes between full and abbreviated forms.
 */
export type IRI = FullIRI | AbbreviatedIRI;

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Create a full IRI from a complete URI string.
 */
export function fullIRI(value: string): FullIRI {
    return { kind: 'FullIRI', value };
}

/**
 * Create an abbreviated IRI from prefix and local name.
 */
export function abbreviatedIRI(prefix: string, localName: string): AbbreviatedIRI {
    return { kind: 'AbbreviatedIRI', prefix, localName };
}

/**
 * Parse an IRI string into either FullIRI or AbbreviatedIRI.
 * If the string contains a colon that isn't part of a URI scheme,
 * it's treated as an abbreviated IRI.
 */
export function parseIRI(iriString: string): IRI {
    // Check if it's a full IRI (starts with http://, https://, urn:, etc.)
    if (iriString.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//)) {
        return fullIRI(iriString);
    }

    // Check for abbreviated IRI (prefix:localName)
    const colonIndex = iriString.indexOf(':');
    if (colonIndex > 0) {
        const prefix = iriString.substring(0, colonIndex);
        const localName = iriString.substring(colonIndex + 1);
        // Ensure it's not a URI scheme (no slashes after colon)
        if (!localName.startsWith('//')) {
            return abbreviatedIRI(prefix, localName);
        }
    }

    // Treat as full IRI by default
    return fullIRI(iriString);
}

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Check if an IRI is a full IRI.
 */
export function isFullIRI(iri: IRI): iri is FullIRI {
    return iri.kind === 'FullIRI';
}

/**
 * Check if an IRI is an abbreviated IRI.
 */
export function isAbbreviatedIRI(iri: IRI): iri is AbbreviatedIRI {
    return iri.kind === 'AbbreviatedIRI';
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Convert an IRI to its string representation.
 * For full IRIs, returns the value.
 * For abbreviated IRIs, returns "prefix:localName".
 */
export function iriToString(iri: IRI): string {
    switch (iri.kind) {
        case 'FullIRI':
            return iri.value;
        case 'AbbreviatedIRI':
            return `${iri.prefix}:${iri.localName}`;
    }
}

/**
 * Extract the local name from an IRI.
 * For full IRIs, extracts the fragment (after #) or last path segment (after /).
 * For abbreviated IRIs, returns the localName.
 */
export function getLocalName(iri: IRI): string {
    switch (iri.kind) {
        case 'FullIRI': {
            const value = iri.value;
            const hashIndex = value.lastIndexOf('#');
            if (hashIndex !== -1) return value.substring(hashIndex + 1);
            const slashIndex = value.lastIndexOf('/');
            if (slashIndex !== -1) return value.substring(slashIndex + 1);
            return value;
        }
        case 'AbbreviatedIRI':
            return iri.localName;
    }
}

/**
 * Extract the namespace from a full IRI.
 * Returns everything up to and including the fragment separator (#) or last slash (/).
 * For abbreviated IRIs, returns undefined (namespace is implicit via prefix).
 */
export function getNamespace(iri: IRI): string | undefined {
    if (iri.kind !== 'FullIRI') return undefined;

    const value = iri.value;
    const hashIndex = value.lastIndexOf('#');
    if (hashIndex !== -1) return value.substring(0, hashIndex + 1);
    const slashIndex = value.lastIndexOf('/');
    if (slashIndex !== -1) return value.substring(0, slashIndex + 1);
    return undefined;
}

/**
 * Compare two IRIs for equality.
 * Note: This only compares structural equality. To compare semantically,
 * abbreviated IRIs should be expanded first using a PrefixManager.
 */
export function iriEquals(a: IRI, b: IRI): boolean {
    if (a.kind !== b.kind) return false;
    if (a.kind === 'FullIRI' && b.kind === 'FullIRI') {
        return a.value === b.value;
    }
    if (a.kind === 'AbbreviatedIRI' && b.kind === 'AbbreviatedIRI') {
        return a.prefix === b.prefix && a.localName === b.localName;
    }
    return false;
}
